import { MarkerFeature } from '../../../utils/models/MarkerFeature';

// Simple browser-compatible EventEmitter implementation
class EventEmitter {
  private events: { [key: string]: Function[] } = {};

  on(event: string, listener: Function): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event: string, listener: Function): void {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(l => l !== listener);
  }

  emit(event: string, ...args: any[]): void {
    if (!this.events[event]) return;
    this.events[event].forEach(listener => listener(...args));
  }

  removeAllListeners(event?: string): void {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }
}

export interface MarkerDragEvent {
  markerId: string;
  marker: MarkerFeature;
  previousPosition: [number, number];
  newPosition: [number, number];
}

export interface LineClickEvent {
  lineId: string;
  routeId: string;
  clickPoint: [number, number];
  insertIndex: number;
}

export interface MarkerClickEvent {
  markerId: string;
  marker: MarkerFeature;
  isRemovalMode: boolean;
}

export interface HoverEvent {
  point: [number, number];
  nearLine: boolean;
  lineId?: string;
  insertPoint?: [number, number];
}

export interface MapClickEvent {
  point: [number, number];
  features: any[];
}

export class MapEventBus extends EventEmitter {
  private static instance: MapEventBus;
  
  private constructor() {
    super();
  }
  
  static getInstance(): MapEventBus {
    if (!MapEventBus.instance) {
      MapEventBus.instance = new MapEventBus();
    }
    return MapEventBus.instance;
  }
  
  // Marker events
  emitMarkerDragStart(markerId: string, marker: MarkerFeature, position: [number, number]): void {
    this.emit('marker:dragstart', { markerId, marker, position });
  }
  
  emitMarkerDragging(event: MarkerDragEvent): void {
    this.emit('marker:dragging', event);
  }
  
  emitMarkerDragEnd(event: MarkerDragEvent): void {
    this.emit('marker:dragend', event);
  }
  
  emitMarkerClick(event: MarkerClickEvent): void {
    this.emit('marker:click', event);
  }
  
  emitMarkerHoverStart(markerId: string, marker: MarkerFeature): void {
    this.emit('marker:hoverstart', { markerId, marker });
  }
  
  emitMarkerHoverEnd(markerId: string): void {
    this.emit('marker:hoverend', { markerId });
  }
  
  // Line events
  emitLineClick(event: LineClickEvent): void {
    this.emit('line:click', event);
  }
  
  emitLineHoverStart(lineId: string, point: [number, number]): void {
    this.emit('line:hoverstart', { lineId, point });
  }
  
  emitLineHoverEnd(lineId: string): void {
    this.emit('line:hoverend', { lineId });
  }
  
  // Map events
  emitMapClick(event: MapClickEvent): void {
    this.emit('map:click', event);
  }
  
  emitMapHover(event: HoverEvent): void {
    this.emit('map:hover', event);
  }
  
  // Listeners with proper typing
  onMarkerDragStart(callback: (event: { markerId: string; marker: MarkerFeature; position: [number, number] }) => void): void {
    this.on('marker:dragstart', callback);
  }
  
  onMarkerDragging(callback: (event: MarkerDragEvent) => void): void {
    this.on('marker:dragging', callback);
  }
  
  onMarkerDragEnd(callback: (event: MarkerDragEvent) => void): void {
    this.on('marker:dragend', callback);
  }
  
  onMarkerClick(callback: (event: MarkerClickEvent) => void): void {
    this.on('marker:click', callback);
  }
  
  onMarkerHoverStart(callback: (event: { markerId: string; marker: MarkerFeature }) => void): void {
    this.on('marker:hoverstart', callback);
  }
  
  onMarkerHoverEnd(callback: (event: { markerId: string }) => void): void {
    this.on('marker:hoverend', callback);
  }
  
  onLineClick(callback: (event: LineClickEvent) => void): void {
    this.on('line:click', callback);
  }
  
  onLineHoverStart(callback: (event: { lineId: string; point: [number, number] }) => void): void {
    this.on('line:hoverstart', callback);
  }
  
  onLineHoverEnd(callback: (event: { lineId: string }) => void): void {
    this.on('line:hoverend', callback);
  }
  
  onMapClick(callback: (event: MapClickEvent) => void): void {
    this.on('map:click', callback);
  }
  
  onMapHover(callback: (event: HoverEvent) => void): void {
    this.on('map:hover', callback);
  }
  
  // Cleanup
  removeAllListenersForEvent(event: string): void {
    this.removeAllListeners(event);
  }
  
  clear(): void {
    this.removeAllListeners();
  }
}